import {
  Component,
  EventEmitter,
  HostListener,
  Output,
  OnInit,
  inject,
} from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import { ButtonRowComponent } from '../../../../../shared/components/button-row/button-row.component';
import { catchError, EMPTY, switchMap } from 'rxjs';
import { LoadingComponent } from '../../../../../shared/components/loading/loading.component';
import { SepTableSelectionService } from '../../../../../shared/components/sep-table/services/sep-table-selection.service';
import { SnackbarService } from '../../../../../core/services/snackbar.service';
import { LicenseInfoCardComponent } from '../../../../../domains/payment/subscription/components/license-info-card/license-info-card.component';
import { CustomPortalError } from '../../../../../core/errors/models/CustomPortalError';
import { CourseEnrollmentTableComponent } from '../../../../../domains/academy/course/components/course-enrollment-table/course-enrollment-table.component';
import { CourseDataService } from '../../../../../domains/academy/course/services/course-data.service';
import { CourseService } from '../../../../../domains/academy/course/services/course.service';
import { EnrollmentContainerBase } from '../EnrollmentContainerBase';
import { EnrollmentNotificationService } from '../../../../../core/services/enrollment-notification.service';

@Component({
  selector: 'app-course-enrollment-container',
  imports: [
    CourseEnrollmentTableComponent,
    TranslocoDirective,
    ButtonRowComponent,
    LoadingComponent,
    LicenseInfoCardComponent,
  ],
  providers: [SepTableSelectionService],
  templateUrl: './course-enrollment-container.component.html',
})
export class CourseEnrollmentContainerComponent
  extends EnrollmentContainerBase
  implements OnInit
{
  private readonly courseDataService = inject(CourseDataService);
  private readonly courseService = inject(CourseService);
  private readonly enrollmentNotificationService = inject(
    EnrollmentNotificationService,
  );
  private readonly snackbarService = inject(SnackbarService);

  @Output() userCreationButtonClickEvent: EventEmitter<void> =
    new EventEmitter<void>();
  @Output() abortButtonClickEvent: EventEmitter<void> =
    new EventEmitter<void>();
  @Output() studentSuccessfullyEnrolledEvent: EventEmitter<void> =
    new EventEmitter<void>();
  @Output() addLicensesButtonClickEvent: EventEmitter<void> =
    new EventEmitter<void>();

  courseId: string | undefined;

  constructor() {
    super();
  }

  ngOnInit(): void {
    this.courseId = this.courseDataService.getCourseId();
    this.getStudentsEligibleForEnrollment();
    this.getAvailableLicenses();
    this.observeTableSelection();
  }

  getStudentsEligibleForEnrollment() {
    if (this.courseId) {
      this.courseService
        .getCourseDetail(this.courseId)
        .pipe(
          switchMap((courseDetail) => {
            const enrolledStudents = courseDetail.courseInstance.students;
            return this.getEligibleStudents(enrolledStudents);
          }),
          catchError((error: CustomPortalError) => {
            this.snackbarService.error(error.errorType);
            return EMPTY;
          }),
        )
        .subscribe();
    }
  }

  enrollStudents() {
    this.isLoading = true;
    const { courseId } = this;
    const students = this.selectedStudents.map((student) => ({
      id: student.id,
    }));
    if (students.length && courseId) {
      this.courseService.enrollStudentsToCourse(students, courseId).subscribe({
        next: () => {
          this.isLoading = false;
          this.enrollmentNotificationService.setEnrollmentChanged();
          this.analyticsService.track('students_enrolled', {
            courseId: courseId,
            students: students,
            participantsAddedCount: students.length,
            numberOfAssignedLicences: this.pendingLicenseAssignments,
          });
          this.studentSuccessfullyEnrolledEvent.emit();
        },
        complete: () => {
          this.snackbarService.success(
            'portal.courses.enrollment.successfulEnrollmentSnackbar',
            {
              count: students.length,
              course: this.languageService.translateOnRuntime(
                'portal.courses.courseTitleKeys.' +
                  this.courseDataService.getCourseName() +
                  '.name',
              ),
            },
          );
        },
        error: (error: CustomPortalError) => {
          this.isLoading = false;
          this.snackbarService.error(error.errorType);
        },
      });
    }
  }

  @HostListener('window:keydown.enter', ['$event'])
  onEnterKey() {
    if (this.selectedStudents.length > 0 && this.canEnroll) {
      this.enrollStudents();
    }
  }
}
